'use client';

import { formatName } from '@pickleballinc/lib/format-name';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { ComponentRef, forwardRef } from 'react';

import { cn } from '../../lib/utils';

export const getPlayerInitials = (opts: { name?: string | null; firstName?: string | null; lastName?: string | null }) => {
	const first = (opts.firstName ?? '').trim();
	const last = (opts.lastName ?? '').trim();

	// Prefer explicit first/last, fall back to parsing a single name string
	if (first || last) {
		return (first.charAt(0) + last.charAt(0) || '?').toUpperCase();
	}

	const parts = (opts.name ?? '').trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return '?';
	if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
	return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
};

type AvatarSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';

const SIZE_CLASSES: Record<AvatarSize, string> = {
	'2xs': 'size-5 text-[8px]',
	xs: 'size-6 text-[10px]',
	sm: 'size-9 text-xs',
	md: 'size-10 text-sm',
	lg: 'size-12 text-xl',
	xl: 'size-16 text-xl',
	'2xl': 'size-20 text-2xl',
	'3xl': 'size-24 text-4xl',
	'4xl': 'size-28 text-4xl',
	'5xl': 'size-32 text-5xl'
};

type AvatarVariant = 'default' | 'transparent' | 'brand' | 'gray' | 'lightGray' | 'darkGray' | 'white';

// Initials-avatar colors from the Pickleball Design System (Avatar → Text, node 19:1012): default gray
// fill with quaternary-gray initials and a subtle contrast ring. Uses the hex values so it stays visible
// in all apps, since in uiv2 is not recognized.
const VARIANT_CLASSES: Record<AvatarVariant, string> = {
	default: 'bg-[#F5F5F5] text-[#717680] ring-1 ring-inset ring-[#E9EAEB]',
	transparent: 'bg-transparent text-white',
	brand: 'bg-purple-100 text-purple-700',
	gray: 'bg-gray-200 text-gray-600',
	lightGray: 'bg-gray-50 text-gray-500',
	darkGray: 'bg-gray-900 text-white',
	white: 'bg-white text-gray-500'
};

export const AvatarImage = forwardRef<ComponentRef<typeof AvatarPrimitive.Image>, React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>>(
	({ className, ...props }, ref) => <AvatarPrimitive.Image ref={ref} className={cn('aspect-square size-full', className)} {...props} />
);
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

export const AvatarFallback = forwardRef<
	ComponentRef<typeof AvatarPrimitive.Fallback>,
	React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
	<AvatarPrimitive.Fallback
		ref={ref}
		className={cn('flex size-full items-center justify-center font-semibold leading-none', className)}
		{...props}
	/>
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

interface AvatarProps extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> {
	name?: string | null;
	firstName?: string | null;
	lastName?: string | null;
	/** Photo URL. Falls back to initials when missing or when it fails to load. */
	src?: string | null;
	defaultSrc?: string | null;
	alt?: string;
	size?: AvatarSize;
	variant?: AvatarVariant;
	/** White ring around the whole avatar (e.g. for overlapping/stacked avatars) */
	hasRing?: boolean;
	fallbackDelayMs?: number;
	className?: string;
	textClassName?: string;
	imageClassName?: string;
}

export const Avatar = forwardRef<ComponentRef<typeof AvatarPrimitive.Root>, AvatarProps>(
	(
		{
			className,
			src,
			defaultSrc,
			alt,
			name,
			firstName,
			lastName,
			size = 'md',
			variant = 'default',
			fallbackDelayMs,
			hasRing = false,
			textClassName,
			imageClassName,
			children,
			...props
		},
		ref
	) => {
		const displayName = formatName({ firstName, lastName }) || name?.trim();
		const imageAlt = alt || (displayName ? `${displayName} Player Image` : 'Player');
		const resolvedSrc = src || (displayName ? undefined : defaultSrc) || undefined;

		return (
			<AvatarPrimitive.Root
				ref={ref}
				className={cn('relative flex shrink-0 overflow-hidden rounded-full', SIZE_CLASSES[size], hasRing && 'ring-2 ring-white', className)}
				{...props}
			>
				{children ?? (
					<>
						<AvatarImage src={resolvedSrc} alt={imageAlt} className={cn('object-cover', VARIANT_CLASSES[variant], imageClassName)} />
						<AvatarFallback delayMs={fallbackDelayMs} className={cn(VARIANT_CLASSES[variant], textClassName)}>
							{getPlayerInitials({ name, firstName, lastName })}
						</AvatarFallback>
					</>
				)}
			</AvatarPrimitive.Root>
		);
	}
);
Avatar.displayName = AvatarPrimitive.Root.displayName;
